import { useQuery } from '@apollo/client';
import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useAppDispatch } from '../../app/redux/useAppSelector';
import QuillContext from '../../editor/QuillContext';
import Sizes from '../../utils/responsive/Sizes';
import isSize from '../../utils/responsive/isSize';
import useSlug from '../../utils/router/useSlug';
import HistorySidebar from './HistorySidebar';
import {
  VersionChanges,
  VersionChangesContent,
  VersionChangesTopBar,
  VersionChangesWrapper,
} from './VersionChanges';
import ResponsiveDate from './ResponsiveDate';
import { gql } from '../../graphql/generated/gql';
import { UIActions } from '../../ui/uiSlice';

const POST_HISTORY_VIEW_QUERY = gql(/** @complexity 2 */ `
  query PostHistoryView($postId: ID!, $versionFrom: Int!, $versionTo: Int!) {
    post(id: $postId) {
      id
      history(versionFrom: $versionFrom, versionTo: $versionTo) {
        id
        insertedAt
      }
      ...VersionChangesWrapper_post
    }
  }
`);

interface InnerPostHistoryViewProps {
  postId: string;
  params: { versionFrom: string; versionTo: string };
}

const InnerPostHistoryView = ({
  postId,
  params,
}: InnerPostHistoryViewProps) => {
  const versionFrom = parseInt(params.versionFrom, 10);
  const versionTo = parseInt(params.versionTo, 10);

  const { data } = useQuery(POST_HISTORY_VIEW_QUERY, {
    variables: {
      postId,
      versionFrom,
      versionTo,
    },
  });

  const post = data?.post;

  return (
    <VersionChanges
      postId={postId}
      versionFrom={versionFrom}
      versionTo={versionTo}
    >
      <VersionChangesWrapper
        initialTitle="History - Slab"
        post={post}
        topbar={
          <VersionChangesTopBar
            versionMeta={
              post?.history ? (
                <ResponsiveDate
                  date={post.history.insertedAt}
                  render={({ condensed, dateWithPreposition }) => {
                    return condensed
                      ? `Edited ${dateWithPreposition}`
                      : `Version edited ${dateWithPreposition}`;
                  }}
                />
              ) : undefined
            }
          />
        }
      >
        <VersionChangesContent />
      </VersionChangesWrapper>
    </VersionChanges>
  );
};

const PostHistoryView = () => {
  const [postId] = useSlug('postSlug');
  const params = useParams() as {
    versionFrom: string;
    versionTo: string;
  };

  const dispatch = useAppDispatch();

  useEffect(() => {
    if (!isSize(Sizes.SM)) {
      dispatch(
        UIActions.openRightPanel(HistorySidebar, { postId, tab: 'versions' }),
      );
    }
    return () => {
      dispatch(UIActions.closeRightPanel());
    };
  }, [dispatch, postId]);

  return (
    <QuillContext>
      <InnerPostHistoryView
        key={`${postId}-${params.versionFrom}-${params.versionTo}`}
        postId={postId}
        params={params}
      />
    </QuillContext>
  );
};

export default PostHistoryView;
